import { Fragment, useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { flexRender } from '@tanstack/react-table'
import { useHistory, useLocation } from 'react-router-dom'
import TableComponent from '../../../../../../components/stories/organisms/table'
import TableToolbar from '../../../../../../components/stories/organisms/table/components/toolbar'
import {
  BodyTd,
  BodyTr
} from '../../../../../../components/stories/organisms/table/styled-components'
import {
  NotFoundContainerBody,
  NotFoundContainerCell,
  NotFoundContainerContent,
  NotFoundContainerRow,
  ProjectMenuTableContainer,
  ProjectMenuTableScrollContainer,
  ProjectMenuTableStatusContent,
  TableProjectsContainer,
  TableProjectsPaginationContainer
} from '../../../../styled-component'
import LeaveProjectAlert from '../../../../components/leave-project-alert'
import { useUserLeaveProjectMutation } from '../../../../../../react-query/project/mutation/use-user-leave-project-mutation'
import { useMyProjectsTable } from './hooks/use-my-projects-table'
import NotFoundComponent from '../../../../../../components/stories/molecules/not-found'
import { Project } from '../../../../../../components/stories/atoms/icons/entities'
import colorsOptions from '../../../../../../components/stories/fundamentals/colors-styles/constants'
import ButtonComponent from '../../../../../../components/stories/atoms/button'
import { buttonVariantOptions } from '../../../../../../components/stories/atoms/button/constants'
import { textStyleSizeOptions } from '../../../../../../components/stories/fundamentals/text-styles/constants'

const MyProjectsTableStatusRow = ({ children, colSpan }) => (
  <BodyTr>
    <BodyTd colSpan={colSpan}>
      <ProjectMenuTableStatusContent>{children}</ProjectMenuTableStatusContent>
    </BodyTd>
  </BodyTr>
)

const MyProjectsTableBody = ({
  canCreateProjects,
  formatMessage,
  isError,
  onCreateProject,
  table,
  search
}) => {
  const visibleRows = table.getRowModel().rows
  const colSpan = table.getAllColumns().length

  if (isError) {
    return (
      <tbody>
        <MyProjectsTableStatusRow colSpan={colSpan}>
          {formatMessage({
            id: 'projectMenu_project_create_error',
            defaultMessage: 'Unable to load projects'
          })}
        </MyProjectsTableStatusRow>
      </tbody>
    )
  }

  if (!visibleRows.length) {
    return (
      <NotFoundContainerBody>
        <NotFoundContainerRow>
          <NotFoundContainerCell colSpan={colSpan}>
            <NotFoundContainerContent>
              {search ? (
                <NotFoundComponent
                  header={formatMessage({ id: 'pinList_noResult' })}
                  headerSize={textStyleSizeOptions['TEXT-XXL']}
                  description={formatMessage({ id: 'not_found_description' })}
                  descriptionSize={textStyleSizeOptions['TEXT-L']}
                />
              ) : (
                <>
                  <NotFoundComponent
                    style={{
                      maxWidth: '432px'
                    }}
                    icon={
                      <Project
                        stroke={colorsOptions.greys['grey-50']}
                        style={{
                          width: '144px',
                          height: '144px'
                        }}
                      />
                    }
                    header={formatMessage({
                      id: canCreateProjects
                        ? 'no_projects_yet'
                        : 'no_projects_yet_permission',
                      defaultMessage: 'No projects yet'
                    })}
                    headerSize={textStyleSizeOptions['TEXT-XXL']}
                    description={formatMessage({
                      id: canCreateProjects
                        ? 'no_projects_yet_description_permission'
                        : 'no_projects_yet_description_no_permission'
                    })}
                    descriptionSize={textStyleSizeOptions['TEXT-L']}
                  />
                  {canCreateProjects && (
                    <ButtonComponent
                      variant={buttonVariantOptions.PRIMARY}
                      onClick={onCreateProject}
                    >
                      {formatMessage({ id: 'create_project' })}
                    </ButtonComponent>
                  )}
                </>
              )}
            </NotFoundContainerContent>
          </NotFoundContainerCell>
        </NotFoundContainerRow>
      </NotFoundContainerBody>
    )
  }

  return (
    <tbody>
      {visibleRows.map((row) => (
        <Fragment key={row.id}>
          <BodyTr>
            {row.getVisibleCells().map((cell) => (
              <BodyTd
                key={cell.id}
                style={{
                  width: cell.column.getSize()
                }}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </BodyTd>
            ))}
          </BodyTr>
        </Fragment>
      ))}
    </tbody>
  )
}

const MyProjectsTablePagination = ({ pagination }) => {
  if (!pagination?.totalElements) return null

  return (
    <TableProjectsPaginationContainer>
      <TableComponent.Pagination pagination={pagination} />
    </TableProjectsPaginationContainer>
  )
}

const MyProjectsList = () => {
  const { formatMessage } = useIntl()
  const history = useHistory()
  const location = useLocation()
  const [projectIdToLeave, setProjectIdToLeave] = useState(null)

  const activeProjectId = useMemo(() => {
    const settingsMatch = location.pathname.match(
      /^\/project\/settings\/([^/]+)/
    )

    if (settingsMatch?.[1]) {
      return settingsMatch[1]
    }

    const projectMatch = location.pathname.match(/^\/project\/([^/]+)/)
    const matchedProjectId = projectMatch?.[1]

    if (
      !matchedProjectId ||
      ['move', 'newfolder', 'rename', 'settings'].includes(matchedProjectId)
    ) {
      return null
    }

    return matchedProjectId
  }, [location.pathname])

  const handleLeaveProjectSuccess = useCallback(
    (_data, { projectId }) => {
      if (activeProjectId === projectId) {
        history.push('/project')
      }
    },
    [activeProjectId, history]
  )

  const { mutate: leaveProject } = useUserLeaveProjectMutation({
    onSuccess: handleLeaveProjectSuccess
  })

  const handleRequestLeaveProject = useCallback((project) => {
    setProjectIdToLeave(project.id)
  }, [])

  const handleOpenCreateProjectModal = useCallback(() => {
    history.push('/project/new')
  }, [history])

  const handleCloseLeaveProjectAlert = useCallback(() => {
    setProjectIdToLeave(null)
  }, [])

  const handleConfirmLeaveProject = useCallback(
    (projectId) => {
      leaveProject({ projectId })
    },
    [leaveProject]
  )

  const {
    canCreateProjects,
    isError,
    pagination,
    pageSize,
    search,
    setPageSize,
    setSearch,
    table
  } = useMyProjectsTable({
    formatMessage,
    onLeaveProject: handleRequestLeaveProject
  })

  return (
    <TableProjectsContainer>
      {projectIdToLeave && (
        <LeaveProjectAlert
          projectId={projectIdToLeave}
          onClose={handleCloseLeaveProjectAlert}
          onConfirm={handleConfirmLeaveProject}
        />
      )}
      <ProjectMenuTableContainer>
        <TableComponent.Root>
          <TableToolbar.Root>
            <TableToolbar.LeftContainer>
              <TableToolbar.Search
                value={search}
                onChangeSearch={setSearch}
                defaultDesktopVariantOpen={!!search}
              />
            </TableToolbar.LeftContainer>
            <TableToolbar.RightContainer>
              <TableToolbar.PageSize
                onChangePageSize={setPageSize}
                pageSize={pageSize}
                style={{
                  paddingRight: '1rem'
                }}
              />
            </TableToolbar.RightContainer>
          </TableToolbar.Root>

          <ProjectMenuTableScrollContainer>
            <TableComponent.Content
              table={table}
              style={{ borderRadius: `0px 0px 16px 16px` }}
            >
              <MyProjectsTableBody
                canCreateProjects={canCreateProjects}
                formatMessage={formatMessage}
                isError={isError}
                onCreateProject={handleOpenCreateProjectModal}
                table={table}
                search={search}
              />
            </TableComponent.Content>
          </ProjectMenuTableScrollContainer>
        </TableComponent.Root>
      </ProjectMenuTableContainer>

      <MyProjectsTablePagination pagination={pagination} />
    </TableProjectsContainer>
  )
}

export default MyProjectsList
